import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUser, FiMail, FiPhone, FiDollarSign, FiLock, FiBell,
  FiCamera, FiX, FiCheck, FiAlertCircle, FiArrowLeft, FiEye, FiEyeOff
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import LoadingScreen from '../shared/LoadingScreen';
import './CoachSettings.css';

// Country phone number configurations
const COUNTRY_CONFIGS = {
  ZA: { code: '+27', name: 'South Africa', format: 'XX XXX XXXX', length: 9, pattern: /^(\d{2})(\d{3})(\d{4})$/ },
  US: { code: '+1', name: 'United States', format: '(XXX) XXX-XXXX', length: 10, pattern: /^(\d{3})(\d{3})(\d{4})$/ },
  GB: { code: '+44', name: 'United Kingdom', format: 'XXXX XXX XXXX', length: 10, pattern: /^(\d{4})(\d{3})(\d{4})$/ },
  AU: { code: '+61', name: 'Australia', format: 'XXX XXX XXX', length: 9, pattern: /^(\d{3})(\d{3})(\d{3})$/ },
  CA: { code: '+1', name: 'Canada', format: '(XXX) XXX-XXXX', length: 10, pattern: /^(\d{3})(\d{3})(\d{4})$/ },
  IN: { code: '+91', name: 'India', format: 'XXXXX XXXXX', length: 10, pattern: /^(\d{5})(\d{5})$/ },
  NZ: { code: '+64', name: 'New Zealand', format: 'XX XXX XXXX', length: 9, pattern: /^(\d{2})(\d{3})(\d{4})$/ },
  KE: { code: '+254', name: 'Kenya', format: 'XXX XXXXXX', length: 9, pattern: /^(\d{3})(\d{6})$/ },
  NG: { code: '+234', name: 'Nigeria', format: 'XXX XXX XXXX', length: 10, pattern: /^(\d{3})(\d{3})(\d{4})$/ },
  AE: { code: '+971', name: 'UAE', format: 'XX XXX XXXX', length: 9, pattern: /^(\d{2})(\d{3})(\d{4})$/ }
};

// Format phone number based on country
const formatPhoneNumber = (cleanDigits, country) => {
  const config = COUNTRY_CONFIGS[country];

  // Limit to max length for country
  const limitedDigits = cleanDigits.slice(0, config.length);

  // Apply formatting
  if (config.pattern.test(limitedDigits)) {
    return limitedDigits.replace(config.pattern, (match, ...groups) => {
      // Filter out undefined groups and join with appropriate spacing
      return groups.filter(g => g !== undefined).join(' ');
    });
  }

  // For partial input, apply partial formatting
  if (limitedDigits.length > 0) {
    const formatParts = config.format.split(' ');
    let result = '';
    let digitIndex = 0;

    for (const part of formatParts) {
      const partLength = part.replace(/[^X]/g, '').length;
      const partDigits = limitedDigits.slice(digitIndex, digitIndex + partLength);

      if (partDigits.length === 0) break;

      if (result.length > 0) result += ' ';
      result += partDigits;
      digitIndex += partLength;

      if (digitIndex >= limitedDigits.length) break;
    }

    return result;
  }

  return limitedDigits;
};

// Parse existing phone number to get country code and display format
const parseExistingPhoneNumber = (fullPhone) => {
  if (!fullPhone) return { countryCode: 'ZA', displayNumber: '' };

  // Clean the input - remove all non-digit characters except the leading +
  const cleaned = fullPhone.trim();

  // Try to match with country codes
  for (const [code, config] of Object.entries(COUNTRY_CONFIGS)) {
    if (cleaned.startsWith(config.code)) {
      // Extract only the digits after the country code
      const digitsOnly = cleaned.substring(config.code.length).replace(/\D/g, '');

      // Limit to the expected length for this country
      const validDigits = digitsOnly.slice(0, config.length);

      // Format the digits
      const formatted = formatPhoneNumber(validDigits, code);
      return { countryCode: code, displayNumber: formatted };
    }
  }

  return { countryCode: 'ZA', displayNumber: '' };
};

const CoachSettings = () => {
  const { currentUser, userProfile, reloadUserProfile } = useAuth();
  const fileInputRef = useRef(null);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('ZA');
  const [phoneInput, setPhoneInput] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [photoURL, setPhotoURL] = useState('');

  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(userProfile?.emailNotifications ?? true);
  const [pushNotifications, setPushNotifications] = useState(userProfile?.pushNotifications ?? true);
  const [clientUpdates, setClientUpdates] = useState(userProfile?.clientUpdates ?? true);

  // UI states
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeSection, setActiveSection] = useState('profile');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Initialize form values when userProfile loads
  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.displayName || '');
      setCurrency(userProfile.currency || 'USD');
      setPhotoURL(userProfile.photoURL || '');

      // Parse and set phone number
      if (userProfile.phoneNumber) {
        const { countryCode: parsedCountry, displayNumber } = parseExistingPhoneNumber(userProfile.phoneNumber);
        setCountryCode(parsedCountry);
        setPhoneInput(displayNumber);
        setPhoneNumber(userProfile.phoneNumber);
      }
    }

    if (currentUser) {
      setEmail(currentUser.email || '');
    }
  }, [userProfile, currentUser]);

  // Handle phone number input change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Extract only digits from input
    const digits = value.replace(/\D/g, '');
    const config = COUNTRY_CONFIGS[countryCode];

    // Don't allow more digits than the country format allows
    if (digits.length <= config.length) {
      // Format the digits
      const formatted = formatPhoneNumber(digits, countryCode);
      setPhoneInput(formatted);

      // Update phoneNumber with full international format
      if (digits.length === config.length) {
        setPhoneNumber(`${config.code}${digits}`);
      } else {
        setPhoneNumber('');
      }
    }
  };

  // Handle country code change
  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountryCode(newCountry);
    setPhoneInput(''); // Clear phone input when country changes
    setPhoneNumber('');
  };

  // Handle profile picture upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);

      // Create a reference to the file location in Firebase Storage
      const storageRef = ref(storage, `profile-photos/${currentUser.uid}/${Date.now()}-${file.name}`);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update photo URL in state
      setPhotoURL(downloadURL);

      // Update in Firebase Auth
      await updateProfile(currentUser, { photoURL: downloadURL });

      // Update in Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        photoURL: downloadURL,
        updatedAt: new Date()
      });

      await reloadUserProfile();
      showMessage('success', 'Profile picture updated successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
      showMessage('error', 'Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle profile info update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);

      // Only include phoneNumber if it's fully entered (has the country code prefix)
      const updates = {
        displayName,
        currency,
        updatedAt: new Date()
      };

      // Only add phone number if it's valid and complete
      if (phoneNumber && phoneNumber.startsWith('+')) {
        updates.phoneNumber = phoneNumber;
      } else if (!phoneInput || phoneInput.trim() === '') {
        // Clear phone number if input is empty
        updates.phoneNumber = '';
      }

      // Update Firestore
      await updateDoc(userDocRef, updates);

      // Update Firebase Auth display name if changed
      if (displayName !== currentUser.displayName) {
        await updateProfile(currentUser, { displayName });
      }

      await reloadUserProfile();
      showMessage('success', 'Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      showMessage('error', 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle email change
  const handleEmailChange = async (e) => {
    e.preventDefault();

    if (!currentPassword) {
      showMessage('error', 'Please enter your current password to change email');
      return;
    }

    setLoading(true);

    try {
      // Reauthenticate user before changing email
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update email in Firebase Auth
      await updateEmail(currentUser, email);

      // Update email in Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        email,
        updatedAt: new Date()
      });

      await reloadUserProfile();
      setCurrentPassword('');
      showMessage('success', 'Email updated successfully. Please verify your new email.');
    } catch (error) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/wrong-password') {
        showMessage('error', 'Incorrect password. Please try again.');
      } else if (error.code === 'auth/email-already-in-use') {
        showMessage('error', 'This email is already in use by another account.');
      } else {
        showMessage('error', 'Failed to update email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Reauthenticate user before changing password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showMessage('success', 'Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        showMessage('error', 'Incorrect current password');
      } else {
        showMessage('error', 'Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle notification settings update
  const handleNotificationUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        emailNotifications,
        pushNotifications,
        clientUpdates,
        updatedAt: new Date()
      });

      await reloadUserProfile();
      showMessage('success', 'Notification preferences updated');
    } catch (error) {
      console.error('Error updating notifications:', error);
      showMessage('error', 'Failed to update notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const renderProfileSection = () => (
    <div className="settings-section">
      <div className="profile-photo-section">
        <div className="photo-preview">
          {photoURL ? (
            <img src={photoURL} alt="Profile" />
          ) : (
            <div className="photo-placeholder">
              {displayName?.charAt(0) || 'U'}
            </div>
          )}
          {uploadingPhoto && (
            <div className="photo-uploading">
              <div className="spinner"></div>
            </div>
          )}
        </div>
        <div className="photo-actions">
          <button
            className="btn-photo"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto}
          >
            <FiCamera />
            {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />
          <p className="photo-hint">JPG, PNG or GIF. Max size 5MB.</p>
        </div>
      </div>

      <form onSubmit={handleProfileUpdate} className="auth-form">
        <div className="form-group">
          <label htmlFor="displayName">Full Name</label>
          <div className="input-wrapper">
            <FiUser className="input-icon" />
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <div className="phone-input-container">
            <select
              className="country-select"
              value={countryCode}
              onChange={handleCountryChange}
              aria-label="Country code"
            >
              {Object.entries(COUNTRY_CONFIGS).map(([code, config]) => (
                <option key={code} value={code}>
                  {config.code} {config.name}
                </option>
              ))}
            </select>
            <div className="input-wrapper phone-input-wrapper">
              <FiPhone className="input-icon" />
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={phoneInput}
                onChange={handlePhoneChange}
                placeholder={COUNTRY_CONFIGS[countryCode].format}
                aria-label="Phone number"
              />
            </div>
          </div>
          <small className="input-hint">
            Format: {COUNTRY_CONFIGS[countryCode].code} {COUNTRY_CONFIGS[countryCode].format}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="currency">Currency</label>
          <div className="input-wrapper">
            <FiDollarSign className="input-icon" />
            <select
              id="currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="currency-select"
            >
              <option value="USD">USD - US Dollar ($)</option>
              <option value="EUR">EUR - Euro (€)</option>
              <option value="GBP">GBP - British Pound (£)</option>
              <option value="CAD">CAD - Canadian Dollar (C$)</option>
              <option value="AUD">AUD - Australian Dollar (A$)</option>
              <option value="JPY">JPY - Japanese Yen (¥)</option>
              <option value="INR">INR - Indian Rupee (₹)</option>
              <option value="BRL">BRL - Brazilian Real (R$)</option>
              <option value="MXN">MXN - Mexican Peso ($)</option>
              <option value="ZAR">ZAR - South African Rand (R)</option>
            </select>
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save Profile'}
        </button>
      </form>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="settings-section">
      <div className="security-subsection">
        <h3 className="subsection-title">Change Email</h3>
        <form onSubmit={handleEmailChange} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">New Email Address</label>
            <div className="input-wrapper">
              <FiMail className="input-icon" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="new.email@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="currentPasswordEmail">Current Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                id="currentPasswordEmail"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update Email'}
          </button>
        </form>
      </div>

      <div className="auth-divider">
        <span>OR</span>
      </div>

      <div className="security-subsection">
        <h3 className="subsection-title">Change Password</h3>
        <form onSubmit={handlePasswordChange} className="auth-form">
          <div className="form-group">
            <label htmlFor="currentPasswordPass">Current Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                id="currentPasswordPass"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="settings-section">
      <form onSubmit={handleNotificationUpdate} className="auth-form">
        <div className="toggle-group">
          <div className="toggle-item">
            <div className="toggle-info">
              <label>Email Notifications</label>
              <p>Receive notifications via email</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="toggle-item">
            <div className="toggle-info">
              <label>Push Notifications</label>
              <p>Receive push notifications in your browser</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={pushNotifications}
                onChange={(e) => setPushNotifications(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="toggle-item">
            <div className="toggle-info">
              <label>Client Updates</label>
              <p>Get notified when clients update their progress</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={clientUpdates}
                onChange={(e) => setClientUpdates(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save Preferences'}
        </button>
      </form>
    </div>
  );

  if (loading && !message.text) {
    return <LoadingScreen />;
  }

  return (
    <div className="auth-container settings-page">
      <div className="auth-wrapper settings-wrapper">
        <div className="auth-card settings-card">
          <div className="auth-header">
            <Link
              to={userProfile?.role === 'trainer' ? '/unique-physique/trainer-dashboard' : '/unique-physique/client-dashboard'}
              className="back-to-login"
            >
              <FiArrowLeft />
              Back to Dashboard
            </Link>
            <h1 className="auth-title">Account Settings</h1>
            <p className="auth-subtitle">Manage your profile and preferences</p>
          </div>

          {message.text && (
            <div className={`auth-error ${message.type === 'success' ? 'auth-success-banner' : ''}`}>
              {message.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
              <span>{message.text}</span>
              <button
                className="close-message-btn"
                onClick={() => setMessage({ type: '', text: '' })}
              >
                <FiX />
              </button>
            </div>
          )}

          <nav className="settings-nav">
            <button
              className={`nav-tab ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              <FiUser />
              <span>Profile</span>
            </button>
            <button
              className={`nav-tab ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => setActiveSection('security')}
            >
              <FiLock />
              <span>Security</span>
            </button>
            <button
              className={`nav-tab ${activeSection === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveSection('notifications')}
            >
              <FiBell />
              <span>Notifications</span>
            </button>
          </nav>

          <div className="settings-content">
            {activeSection === 'profile' && renderProfileSection()}
            {activeSection === 'security' && renderSecuritySection()}
            {activeSection === 'notifications' && renderNotificationsSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachSettings;
